import { api } from './client';
import { Product as FrontendProduct, Category, Subcategory, Sector, SUBCATEGORY_LABELS, SECTOR_LABELS } from '../data/products';

export interface Category {
  id: number;
  slug: string;
  name_fr: string;
  name_en: string;
}

export interface Subcategory {
  id: number;
  slug: string;
  name_fr: string;
  name_en: string;
  category_id: number;
}

export interface Product {
  id: string;
  category_id: number;
  subcategory_id: number;
  brand: string;
  ref: string;
  name?: string;
  name_fr?: string;
  name_en: string;
  description?: string;
  description_fr?: string;
  description_en?: string;
  price: number;
  currency: string;
  stock: number;
  moq: number;
  quote_threshold: number;
  features: string[];
  features_en: string[];
  images: string[];
  variants: { sizes?: string[]; colors?: string[] };
  norm_ids: number[];
  sector_ids: number[];
  bestseller: boolean;
  badge?: string;
  badge_en?: string;
  is_active: boolean;
  category?: Category;
  subcategory?: Subcategory;
  created_at?: string;
  updated_at?: string;
}

export function transformProduct(apiProduct: Product): FrontendProduct {
  const categorySlug = apiProduct.category?.slug || '';
  const subcategorySlug = apiProduct.subcategory?.slug || '';
  
  const sectors: Sector[] = [];
  if (apiProduct.sector_ids && apiProduct.sector_ids.length > 0) {
    const sectorMap: Record<number, Sector> = {
      1: 'industrie',
      2: 'medical',
      3: 'restauration',
      4: 'artisanat',
      5: 'chantier',
    };
    apiProduct.sector_ids.forEach((id: number) => {
      if (sectorMap[id]) sectors.push(sectorMap[id]);
    });
  }
  
  const norms: string[] = [];
  if (apiProduct.norm_ids && apiProduct.norm_ids.length > 0) {
    const normMap: Record<number, string> = {
      1: 'EN ISO 20345',
      2: 'EN ISO 20344',
      3: 'EN ISO 20347',
      4: 'EN 374',
      5: 'EN 166',
      6: 'EN 352',
      7: 'EN 149',
      8: 'EN 471',
      9: 'EN 1149',
      10: 'EN 1073',
      11: 'EN 1082',
      12: 'ISO 13485',
      13: 'IEC 60601',
    };
    apiProduct.norm_ids.forEach((id: number) => {
      if (normMap[id]) norms.push(normMap[id]);
    });
  }
  
  return {
    id: String(apiProduct.id),
    category: (categorySlug || 'vetements') as Category,
    subcategory: (subcategorySlug.replace(/-/g, '') || 'vestes') as Subcategory,
    sector: sectors,
    name: apiProduct.name_fr || apiProduct.name || '',
    nameEn: apiProduct.name_en || apiProduct.name_fr || apiProduct.name || '',
    brand: apiProduct.brand,
    ref: apiProduct.ref,
    price: apiProduct.price,
    currency: apiProduct.currency,
    stock: apiProduct.stock,
    moq: apiProduct.moq,
    quoteThreshold: apiProduct.quote_threshold,
    norm: norms,
    description: apiProduct.description_fr || apiProduct.description || '',
    descriptionEn: apiProduct.description_en || apiProduct.description_fr || apiProduct.description || '',
    images: apiProduct.images,
    variants: apiProduct.variants,
    features: apiProduct.features,
    featuresEn: apiProduct.features_en,
    bestseller: apiProduct.bestseller,
    badge: apiProduct.badge,
    badgeEn: apiProduct.badge_en,
  };
}

export interface ProductListResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface CategoriesWithSubcategories {
  id: number;
  slug: string;
  name_fr: string;
  name_en: string;
  subcategories: Subcategory[];
}

export interface FiltersMeta {
  brands: string[];
  norms: { id: number; label: string }[];
  price_min: number;
  price_max: number;
  categories: Category[];
}

export interface ProductsQueryParams {
  category?: string;
  subcategory?: string;
  sector?: string;
  brand?: string;
  norm?: string;
  price_min?: number;
  price_max?: number;
  stock_only?: boolean;
  purchase_mode?: string;
  q?: string;
  search?: string;
  sort_by?: string;
  sort_order?: string;
  page?: number;
  limit?: number;
}

export const productsApi = {
  getProducts: (params: ProductsQueryParams = {}) =>
    api.get<ProductListResponse>('/products', params),

  getProduct: (id: string) =>
    api.get<Product>(`/products/${id}`),

  getBestsellers: (limit: number = 10) =>
    api.get<ProductListResponse>('/products/bestsellers/list', { limit }),

  getCategories: () =>
    api.get<{ categories: CategoriesWithSubcategories[] }>('/products/categories/list'),

  getFiltersMeta: () =>
    api.get<FiltersMeta>('/products/filters/meta'),
};
